/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { Body_server_side_upload_v1_workspace__slug__record__id__upload_fallback_post } from '../models/Body_server_side_upload_v1_workspace__slug__record__id__upload_fallback_post';
import type { Body_update_phone_number_v1_workspace__slug__record__id__phone_number_put } from '../models/Body_update_phone_number_v1_workspace__slug__record__id__phone_number_put';
import type { Body_update_transcribe_config_v1_workspace__slug__record__id__transcribe_config_put } from '../models/Body_update_transcribe_config_v1_workspace__slug__record__id__transcribe_config_put';
import type { ConfluenceExportParams } from '../models/ConfluenceExportParams';
import type { ConversationShare } from '../models/ConversationShare';
import type { Dialog } from '../models/Dialog';
import type { DialogUpdate } from '../models/DialogUpdate';
import type { InsightTranslationRequest } from '../models/InsightTranslationRequest';
import type { LinearIssueCreateParams } from '../models/LinearIssueCreateParams';
import type { Monologist } from '../models/Monologist';
import type { MSGraphChannelExportParam } from '../models/MSGraphChannelExportParam';
import type { MSGraphChatExportParam } from '../models/MSGraphChatExportParam';
import type { NotionExportParams } from '../models/NotionExportParams';
import type { Paging_RecordWithRecordPermission_ } from '../models/Paging_RecordWithRecordPermission_';
import type { PipedriveExportParam } from '../models/PipedriveExportParam';
import type { RecordAccessUpdate } from '../models/RecordAccessUpdate';
import type { RecordCreateRequest } from '../models/RecordCreateRequest';
import type { RecordCustomInsight } from '../models/RecordCustomInsight';
import type { RecordCustomInsightCreateRequest } from '../models/RecordCustomInsightCreateRequest';
import type { RecordCustomInsightTranslateRequest } from '../models/RecordCustomInsightTranslateRequest';
import type { RecordDetail } from '../models/RecordDetail';
import type { RecordExportHistory } from '../models/RecordExportHistory';
import type { RecordHedwigExportRequest } from '../models/RecordHedwigExportRequest';
import type { RecordInsight } from '../models/RecordInsight';
import type { RecordInsightLanguage } from '../models/RecordInsightLanguage';
import type { RecordInsightUpdateRequest } from '../models/RecordInsightUpdateRequest';
import type { RecordSlackExportRequest } from '../models/RecordSlackExportRequest';
import type { RecordTranscribeRequest } from '../models/RecordTranscribeRequest';
import type { RecordUpdate } from '../models/RecordUpdate';
import type { RecordUploadComplete } from '../models/RecordUploadComplete';
import type { RecordWithRecordPermission } from '../models/RecordWithRecordPermission';
import type { RelatedRecord } from '../models/RelatedRecord';
import type { SalesforceExportParams } from '../models/SalesforceExportParams';
import type { SalesforceExportParamsV2 } from '../models/SalesforceExportParamsV2';
import type { SharedRecord } from '../models/SharedRecord';
import type { SharedRecordPutRequest } from '../models/SharedRecordPutRequest';
import type { SpeakerInfoValue } from '../models/SpeakerInfoValue';
import type { SpeakerInfoValueSchema } from '../models/SpeakerInfoValueSchema';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class RecordService {
  constructor(public readonly httpRequest: BaseHttpRequest) {}

  /**
   * List Record
   * 참고: `dialogs`는 제외하고 내려감.
   * @param slug
   * @param query 검색 키워드
   * @param userId 대화에 참여한 user의 id
   * @param labelIds 레이블 id 리스트
   * @param labelFilterCondition 레이블 필터링 조건
   * @param order 레코드 정렬 기준 (내림차순)
   * @param teamId 팀 id
   * @param nextToken pagination token
   * @param ownerUserId 소유자 user id
   * @param ownerUserIds 소유자 user id 리스트
   * @param recordSources 레코드 소스 리스트
   * @param startDate 시작 날짜 (YYYY-MM-DD)
   * @param endDate 끝 날짜 (YYYY-MM-DD)
   * @returns Paging_RecordWithRecordPermission_ Successful Response
   * @throws ApiError
   */
  public listRecordV1WorkspaceSlugRecordGet(
    slug: string,
    query?: string | null,
    userId?: number | null,
    labelIds?: Array<number> | null,
    labelFilterCondition: 'and' | 'or' = 'and',
    order: 'id' | 'rec_date' = 'id',
    teamId?: number | null,
    nextToken?: string | null,
    ownerUserId?: number | null,
    ownerUserIds?: Array<number> | null,
    recordSources?: Array<
      | 'zoom'
      | 'google_meet'
      | 'teams_meet'
      | 'phone_call'
      | 'voice_recorder'
      | 'screen_recorder'
      | 'manual_upload'
    > | null,
    startDate?: string | null,
    endDate?: string | null
  ): CancelablePromise<Paging_RecordWithRecordPermission_> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/v1/workspace/{slug}/record',
      path: {
        slug: slug,
      },
      query: {
        query: query,
        user_id: userId,
        label_ids: labelIds,
        label_filter_condition: labelFilterCondition,
        order: order,
        team_id: teamId,
        next_token: nextToken,
        owner_user_id: ownerUserId,
        owner_user_ids: ownerUserIds,
        record_sources: recordSources,
        start_date: startDate,
        end_date: endDate,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Create Record
   * @param slug
   * @param requestBody
   * @returns RecordWithRecordPermission Successful Response
   * @throws ApiError
   */
  public createRecordV1WorkspaceSlugRecordPost(
    slug: string,
    requestBody: RecordCreateRequest
  ): CancelablePromise<RecordWithRecordPermission> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/v1/workspace/{slug}/record',
      path: {
        slug: slug,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Get Record
   * @param slug
   * @param id
   * @param query 검색어: 막걸리 보내주세요
   * @returns any Successful Response
   * @throws ApiError
   */
  public getRecordV1WorkspaceSlugRecordIdGet(
    slug: string,
    id: number,
    query?: string | null
  ): CancelablePromise<RecordWithRecordPermission | RecordDetail> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/v1/workspace/{slug}/record/{id}',
      path: {
        slug: slug,
        id: id,
      },
      query: {
        query: query,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Update Record
   * @param slug
   * @param id
   * @param requestBody
   * @returns RecordWithRecordPermission Successful Response
   * @throws ApiError
   */
  public updateRecordV1WorkspaceSlugRecordIdPatch(
    slug: string,
    id: number,
    requestBody: RecordUpdate
  ): CancelablePromise<RecordWithRecordPermission> {
    return this.httpRequest.request({
      method: 'PATCH',
      url: '/v1/workspace/{slug}/record/{id}',
      path: {
        slug: slug,
        id: id,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Delete Record
   * @param slug
   * @param id
   * @returns void
   * @throws ApiError
   */
  public deleteRecordV1WorkspaceSlugRecordIdDelete(
    slug: string,
    id: number
  ): CancelablePromise<void> {
    return this.httpRequest.request({
      method: 'DELETE',
      url: '/v1/workspace/{slug}/record/{id}',
      path: {
        slug: slug,
        id: id,
      },
      errors: {
        422: `Validation Error`,
        425: `Too Early`,
      },
    });
  }

  /**
   * @deprecated
   * Update Record Access
   * @param slug
   * @param id
   * @param requestBody
   * @returns RecordWithRecordPermission Successful Response
   * @throws ApiError
   */
  public updateRecordAccessV1WorkspaceSlugRecordIdAccessPatch(
    slug: string,
    id: number,
    requestBody: RecordAccessUpdate
  ): CancelablePromise<RecordWithRecordPermission> {
    return this.httpRequest.request({
      method: 'PATCH',
      url: '/v1/workspace/{slug}/record/{id}/access',
      path: {
        slug: slug,
        id: id,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Update Transcribe Config
   * 레코드에 대한 문자변환 설정을 업데이트합니다.
   * 변환이 되기 전에 없데이트 된 값만 유효하게 적용됩니다.
   * (complete upload 이후에 업데이트한 내역은 재전사 할 경우에만 적용됩니다.)
   * @param slug
   * @param id
   * @param requestBody
   * @returns void
   * @throws ApiError
   */
  public updateTranscribeConfigV1WorkspaceSlugRecordIdTranscribeConfigPut(
    slug: string,
    id: number,
    requestBody?: Body_update_transcribe_config_v1_workspace__slug__record__id__transcribe_config_put
  ): CancelablePromise<void> {
    return this.httpRequest.request({
      method: 'PUT',
      url: '/v1/workspace/{slug}/record/{id}/transcribe-config',
      path: {
        slug: slug,
        id: id,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Initiate Upload
   * 응답의 S3 Presigned URL에 업로드
   * - HTTP Method: PUT
   * - HTTP Header:
   * - `Content-Length`: record 생성시 입력한 `filesize` 값과 동일해야 함
   * - `Content-Type`: 파일 확장자에 따라 적절한 `mimetype`을 지정해야 함 (ex: `audio/mpeg`, `video/mp4`)
   * - `Content-Disposition`: `"attachment; filename*=UTF-8''" + URLEncode(filename)`
   * - curl 예시
   * ```bash
   * curl -XPUT \
   * --data-binary '@뉴스.mp3' \
   * -H 'Content-Type: audio/mpeg' \
   * -H "Content-Disposition: attachment; filename*=UTF-8''%EB%89%B4%EC%8A%A4.mp3" \
   * $PRESIGNED_URL
   * ```
   * @param slug
   * @param id
   * @returns string Successful Response
   * @throws ApiError
   */
  public initiateUploadV1WorkspaceSlugRecordIdUploadPost(
    slug: string,
    id: number
  ): CancelablePromise<string> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/v1/workspace/{slug}/record/{id}/upload',
      path: {
        slug: slug,
        id: id,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Complete Upload
   * @param slug
   * @param id
   * @param requestBody
   * @returns RecordWithRecordPermission Successful Response
   * @throws ApiError
   */
  public completeUploadV1WorkspaceSlugRecordIdUploadCompletePost(
    slug: string,
    id: number,
    requestBody?: RecordUploadComplete | null
  ): CancelablePromise<RecordWithRecordPermission> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/v1/workspace/{slug}/record/{id}/upload/complete',
      path: {
        slug: slug,
        id: id,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Server Side Upload
   * 클라이언트가 파일을 서버로 업로드하면, 서버가 S3에 업로드합니다.
   * @param slug
   * @param id
   * @param formData
   * @returns any Successful Response
   * @throws ApiError
   */
  public serverSideUploadV1WorkspaceSlugRecordIdUploadFallbackPost(
    slug: string,
    id: number,
    formData: Body_server_side_upload_v1_workspace__slug__record__id__upload_fallback_post
  ): CancelablePromise<any> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/v1/workspace/{slug}/record/{id}/upload/fallback',
      path: {
        slug: slug,
        id: id,
      },
      formData: formData,
      mediaType: 'multipart/form-data',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Transcribe
   * @param slug
   * @param id
   * @param requestBody
   * @returns RecordWithRecordPermission Successful Response
   * @throws ApiError
   */
  public transcribeV1WorkspaceSlugRecordIdTranscribePost(
    slug: string,
    id: number,
    requestBody: RecordTranscribeRequest
  ): CancelablePromise<RecordWithRecordPermission> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/v1/workspace/{slug}/record/{id}/transcribe',
      path: {
        slug: slug,
        id: id,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        412: `Precondition Failed`,
        422: `Validation Error`,
        425: `Too Early`,
      },
    });
  }

  /**
   * Download
   * @param slug
   * @param id
   * @returns string Successful Response
   * @throws ApiError
   */
  public downloadV1WorkspaceSlugRecordIdDownloadGet(
    slug: string,
    id: number
  ): CancelablePromise<string> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/v1/workspace/{slug}/record/{id}/download',
      path: {
        slug: slug,
        id: id,
      },
      errors: {
        404: `Not Found`,
        422: `Validation Error`,
        425: `Too Early`,
      },
    });
  }

  /**
   * Subtitle
   * @param ext
   * @param slug
   * @param id
   * @returns any Successful Response
   * @throws ApiError
   */
  public subtitleV1WorkspaceSlugRecordIdSubtitleExtGet(
    ext: 'vtt' | 'srt',
    slug: string,
    id: number
  ): CancelablePromise<any> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/v1/workspace/{slug}/record/{id}/subtitle.{ext}',
      path: {
        ext: ext,
        slug: slug,
        id: id,
      },
      errors: {
        422: `Validation Error`,
        425: `Too Early`,
      },
    });
  }

  /**
   * Update Dialog
   * @param slug
   * @param id
   * @param requestBody
   * @returns Dialog Successful Response
   * @throws ApiError
   */
  public updateDialogV1WorkspaceSlugRecordIdDialogPatch(
    slug: string,
    id: number,
    requestBody: Array<DialogUpdate>
  ): CancelablePromise<Array<Dialog>> {
    return this.httpRequest.request({
      method: 'PATCH',
      url: '/v1/workspace/{slug}/record/{id}/dialog',
      path: {
        slug: slug,
        id: id,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Update Speaker Info
   * @param slug
   * @param id
   * @param requestBody
   * @returns SpeakerInfoValue Successful Response
   * @throws ApiError
   */
  public updateSpeakerInfoV1WorkspaceSlugRecordIdSpeakerInfoPatch(
    slug: string,
    id: number,
    requestBody: Record<string, SpeakerInfoValueSchema>
  ): CancelablePromise<Record<string, SpeakerInfoValue>> {
    return this.httpRequest.request({
      method: 'PATCH',
      url: '/v1/workspace/{slug}/record/{id}/speaker-info',
      path: {
        slug: slug,
        id: id,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Update Phone Number
   * @param slug
   * @param id
   * @param requestBody
   * @returns any Successful Response
   * @throws ApiError
   */
  public updatePhoneNumberV1WorkspaceSlugRecordIdPhoneNumberPut(
    slug: string,
    id: number,
    requestBody: Body_update_phone_number_v1_workspace__slug__record__id__phone_number_put
  ): CancelablePromise<any> {
    return this.httpRequest.request({
      method: 'PUT',
      url: '/v1/workspace/{slug}/record/{id}/phone-number',
      path: {
        slug: slug,
        id: id,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `Bad Request`,
        403: `Forbidden`,
        422: `Validation Error`,
      },
    });
  }

  /**
   * Get Conversation Shares
   * 레코드의 대화 점유율 통계를 가져옵니다.
   * @param slug
   * @param id
   * @returns ConversationShare Successful Response
   * @throws ApiError
   */
  public getConversationSharesV1WorkspaceSlugRecordIdConversationSharesGet(
    slug: string,
    id: number
  ): CancelablePromise<Array<ConversationShare>> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/v1/workspace/{slug}/record/{id}/conversation-shares',
      path: {
        slug: slug,
        id: id,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Get Monologists
   * 레코드의 대화 독점 비율 통계를 가져옵니다.
   * @param slug
   * @param id
   * @returns Monologist Successful Response
   * @throws ApiError
   */
  public getMonologistsV1WorkspaceSlugRecordIdMonologistsGet(
    slug: string,
    id: number
  ): CancelablePromise<Array<Monologist>> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/v1/workspace/{slug}/record/{id}/monologists',
      path: {
        slug: slug,
        id: id,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Get Insight Language
   * 레코드의 인사이트의 언어를 가져옵니다.
   * @param slug
   * @param id
   * @returns RecordInsightLanguage Successful Response
   * @throws ApiError
   */
  public getInsightLanguageV1WorkspaceSlugRecordIdInsightLanguageGet(
    slug: string,
    id: number
  ): CancelablePromise<RecordInsightLanguage> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/v1/workspace/{slug}/record/{id}/insight/language',
      path: {
        slug: slug,
        id: id,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Get Insight
   * 레코드의 인사이트를 가져옵니다.
   * @param insightType
   * @param slug
   * @param id
   * @returns RecordInsight Successful Response
   * @throws ApiError
   */
  public getInsightV1WorkspaceSlugRecordIdInsightInsightTypeGet(
    insightType:
      | 'toc'
      | 'action_item'
      | 'keyword'
      | 'date_time'
      | 'summary'
      | 'takeaway'
      | 'action_item_ts',
    slug: string,
    id: number
  ): CancelablePromise<RecordInsight> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/v1/workspace/{slug}/record/{id}/insight/{insight_type}',
      path: {
        insight_type: insightType,
        slug: slug,
        id: id,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Update Insight
   * @param insightType
   * @param slug
   * @param id
   * @param requestBody
   * @returns RecordInsight Successful Response
   * @throws ApiError
   */
  public updateInsightV1WorkspaceSlugRecordIdInsightInsightTypePatch(
    insightType:
      | 'toc'
      | 'action_item'
      | 'keyword'
      | 'date_time'
      | 'summary'
      | 'takeaway'
      | 'action_item_ts',
    slug: string,
    id: number,
    requestBody: RecordInsightUpdateRequest
  ): CancelablePromise<RecordInsight> {
    return this.httpRequest.request({
      method: 'PATCH',
      url: '/v1/workspace/{slug}/record/{id}/insight/{insight_type}',
      path: {
        insight_type: insightType,
        slug: slug,
        id: id,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        404: `Insight not found`,
        405: `This insight_type does not allow updating insight.`,
        412: `Insight update is not allowed for this insight status or count`,
        413: `Insight update request is too long`,
        422: `Update value has invalid type for this insight_type`,
      },
    });
  }

  /**
   * Get Insights
   * 레코드의 인사이트를 가져옵니다.
   * @param slug
   * @param id
   * @returns RecordInsight Successful Response
   * @throws ApiError
   */
  public getInsightsV1WorkspaceSlugRecordIdInsightGet(
    slug: string,
    id: number
  ): CancelablePromise<Array<RecordInsight>> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/v1/workspace/{slug}/record/{id}/insight',
      path: {
        slug: slug,
        id: id,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Export To Slack
   * @param slug
   * @param id
   * @param requestBody
   * @returns any Successful Response
   * @throws ApiError
   */
  public exportToSlackV1WorkspaceSlugRecordIdSlackPost(
    slug: string,
    id: number,
    requestBody: RecordSlackExportRequest
  ): CancelablePromise<any> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/v1/workspace/{slug}/record/{id}/slack',
      path: {
        slug: slug,
        id: id,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Export To Salesforce
   * @param slug
   * @param id
   * @param requestBody
   * @returns any Successful Response
   * @throws ApiError
   */
  public exportToSalesforceV1WorkspaceSlugRecordIdSalesforcePost(
    slug: string,
    id: number,
    requestBody: SalesforceExportParamsV2 | SalesforceExportParams
  ): CancelablePromise<any> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/v1/workspace/{slug}/record/{id}/salesforce',
      path: {
        slug: slug,
        id: id,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Export To Pipedrive
   * @param slug
   * @param id
   * @param requestBody
   * @returns any Successful Response
   * @throws ApiError
   */
  public exportToPipedriveV1WorkspaceSlugRecordIdPipedrivePost(
    slug: string,
    id: number,
    requestBody: PipedriveExportParam
  ): CancelablePromise<any> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/v1/workspace/{slug}/record/{id}/pipedrive',
      path: {
        slug: slug,
        id: id,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Export To Teams
   * @param slug
   * @param id
   * @param requestBody
   * @returns any Successful Response
   * @throws ApiError
   */
  public exportToTeamsV1WorkspaceSlugRecordIdMsTeamsPost(
    slug: string,
    id: number,
    requestBody: MSGraphChatExportParam | MSGraphChannelExportParam
  ): CancelablePromise<any> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/v1/workspace/{slug}/record/{id}/ms-teams',
      path: {
        slug: slug,
        id: id,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Export To Notion
   * @param slug
   * @param id
   * @param requestBody
   * @returns any Successful Response
   * @throws ApiError
   */
  public exportToNotionV1WorkspaceSlugRecordIdNotionPost(
    slug: string,
    id: number,
    requestBody?: NotionExportParams | null
  ): CancelablePromise<any> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/v1/workspace/{slug}/record/{id}/notion',
      path: {
        slug: slug,
        id: id,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Export To Confluence
   * @param slug
   * @param id
   * @param requestBody
   * @returns any Successful Response
   * @throws ApiError
   */
  public exportToConfluenceV1WorkspaceSlugRecordIdConfluencePost(
    slug: string,
    id: number,
    requestBody?: ConfluenceExportParams | null
  ): CancelablePromise<any> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/v1/workspace/{slug}/record/{id}/confluence',
      path: {
        slug: slug,
        id: id,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Create Issue Linear
   * @param slug
   * @param id
   * @param requestBody
   * @returns any Successful Response
   * @throws ApiError
   */
  public createIssueLinearV1WorkspaceSlugRecordIdLinearPost(
    slug: string,
    id: number,
    requestBody?: LinearIssueCreateParams | null
  ): CancelablePromise<any> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/v1/workspace/{slug}/record/{id}/linear',
      path: {
        slug: slug,
        id: id,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Export To Email
   * @param slug
   * @param id
   * @param requestBody
   * @returns any Successful Response
   * @throws ApiError
   */
  public exportToEmailV1WorkspaceSlugRecordIdEmailPost(
    slug: string,
    id: number,
    requestBody: RecordHedwigExportRequest
  ): CancelablePromise<any> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/v1/workspace/{slug}/record/{id}/email',
      path: {
        slug: slug,
        id: id,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Add Record Label
   * @param slug
   * @param id
   * @param labelId
   * @returns void
   * @throws ApiError
   */
  public addRecordLabelV1WorkspaceSlugRecordIdLabelLabelIdPost(
    slug: string,
    id: number,
    labelId: number
  ): CancelablePromise<void> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/v1/workspace/{slug}/record/{id}/label/{label_id}',
      path: {
        slug: slug,
        id: id,
        label_id: labelId,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Remove Record Label
   * @param slug
   * @param id
   * @param labelId
   * @returns void
   * @throws ApiError
   */
  public removeRecordLabelV1WorkspaceSlugRecordIdLabelLabelIdDelete(
    slug: string,
    id: number,
    labelId: number
  ): CancelablePromise<void> {
    return this.httpRequest.request({
      method: 'DELETE',
      url: '/v1/workspace/{slug}/record/{id}/label/{label_id}',
      path: {
        slug: slug,
        id: id,
        label_id: labelId,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Get Insights Translation
   * @param slug
   * @param id
   * @param requestBody
   * @returns RecordInsight Successful Response
   * @throws ApiError
   */
  public getInsightsTranslationV1WorkspaceSlugRecordIdInsightsTranslationPost(
    slug: string,
    id: number,
    requestBody: InsightTranslationRequest
  ): CancelablePromise<Array<RecordInsight>> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/v1/workspace/{slug}/record/{id}/insights/translation',
      path: {
        slug: slug,
        id: id,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Get Insight Translation
   * @param insightType
   * @param slug
   * @param id
   * @param requestBody
   * @returns RecordInsight Successful Response
   * @throws ApiError
   */
  public getInsightTranslationV1WorkspaceSlugRecordIdInsightInsightTypeTranslationPost(
    insightType:
      | 'toc'
      | 'action_item'
      | 'keyword'
      | 'date_time'
      | 'summary'
      | 'takeaway'
      | 'action_item_ts',
    slug: string,
    id: number,
    requestBody: InsightTranslationRequest
  ): CancelablePromise<RecordInsight> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/v1/workspace/{slug}/record/{id}/insight/{insight_type}/translation',
      path: {
        insight_type: insightType,
        slug: slug,
        id: id,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Get Shared Record
   * @param slug
   * @param id
   * @returns SharedRecord Successful Response
   * @throws ApiError
   */
  public getSharedRecordV1WorkspaceSlugRecordIdSharedGet(
    slug: string,
    id: number
  ): CancelablePromise<SharedRecord> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/v1/workspace/{slug}/record/{id}/shared',
      path: {
        slug: slug,
        id: id,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Put Shared Record
   * 본 라우터 서비스로 리팩토링 필요.
   * @param slug
   * @param id
   * @param requestBody
   * @returns SharedRecord Successful Response
   * @throws ApiError
   */
  public putSharedRecordV1WorkspaceSlugRecordIdSharedPut(
    slug: string,
    id: number,
    requestBody: SharedRecordPutRequest
  ): CancelablePromise<SharedRecord> {
    return this.httpRequest.request({
      method: 'PUT',
      url: '/v1/workspace/{slug}/record/{id}/shared',
      path: {
        slug: slug,
        id: id,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Delete Shared Record
   * @param slug
   * @param id
   * @returns void
   * @throws ApiError
   */
  public deleteSharedRecordV1WorkspaceSlugRecordIdSharedDelete(
    slug: string,
    id: number
  ): CancelablePromise<void> {
    return this.httpRequest.request({
      method: 'DELETE',
      url: '/v1/workspace/{slug}/record/{id}/shared',
      path: {
        slug: slug,
        id: id,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * List Shared Record
   * 참고: `dialogs`는 제외하고 내려감.
   * @param slug
   * @param nextToken pagination token
   * @returns Paging_RecordWithRecordPermission_ Successful Response
   * @throws ApiError
   */
  public listSharedRecordV1WorkspaceSlugSharedRecordGet(
    slug: string,
    nextToken?: string | null
  ): CancelablePromise<Paging_RecordWithRecordPermission_> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/v1/workspace/{slug}/shared-record',
      path: {
        slug: slug,
      },
      query: {
        next_token: nextToken,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Delete Record Media
   * @param slug
   * @param id
   * @returns RecordWithRecordPermission Successful Response
   * @throws ApiError
   */
  public deleteRecordMediaV1WorkspacesSlugRecordsIdMediaDelete(
    slug: string,
    id: number
  ): CancelablePromise<RecordWithRecordPermission> {
    return this.httpRequest.request({
      method: 'DELETE',
      url: '/v1/workspaces/{slug}/records/{id}/media',
      path: {
        slug: slug,
        id: id,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Delete Record Dialogs
   * @param slug
   * @param id
   * @returns RecordWithRecordPermission Successful Response
   * @throws ApiError
   */
  public deleteRecordDialogsV1WorkspacesSlugRecordsIdDialogsDelete(
    slug: string,
    id: number
  ): CancelablePromise<RecordWithRecordPermission> {
    return this.httpRequest.request({
      method: 'DELETE',
      url: '/v1/workspaces/{slug}/records/{id}/dialogs',
      path: {
        slug: slug,
        id: id,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Related Records
   * @param slug
   * @param id
   * @returns RelatedRecord Successful Response
   * @throws ApiError
   */
  public relatedRecordsV1WorkspacesSlugRecordsIdRelatedGet(
    slug: string,
    id: number
  ): CancelablePromise<Array<RelatedRecord>> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/v1/workspaces/{slug}/records/{id}/related',
      path: {
        slug: slug,
        id: id,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Get Record Custom Insights
   * @param slug
   * @param recordId
   * @returns RecordCustomInsight Successful Response
   * @throws ApiError
   */
  public getRecordCustomInsightsV1WorkspacesSlugRecordsRecordIdCustomInsightsGet(
    slug: string,
    recordId: number
  ): CancelablePromise<Array<RecordCustomInsight>> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/v1/workspaces/{slug}/records/{record_id}/custom-insights',
      path: {
        slug: slug,
        record_id: recordId,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Create Record Custom Insight
   * @param slug
   * @param recordId
   * @param requestBody
   * @returns RecordCustomInsight Successful Response
   * @throws ApiError
   */
  public createRecordCustomInsightV1WorkspacesSlugRecordsRecordIdCustomInsightsPost(
    slug: string,
    recordId: number,
    requestBody: RecordCustomInsightCreateRequest
  ): CancelablePromise<RecordCustomInsight> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/v1/workspaces/{slug}/records/{record_id}/custom-insights',
      path: {
        slug: slug,
        record_id: recordId,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Delete Record Custom Insight
   * @param id
   * @param slug
   * @param recordId
   * @returns void
   * @throws ApiError
   */
  public deleteRecordCustomInsightV1WorkspacesSlugRecordsRecordIdCustomInsightsIdDelete(
    id: number,
    slug: string,
    recordId: number
  ): CancelablePromise<void> {
    return this.httpRequest.request({
      method: 'DELETE',
      url: '/v1/workspaces/{slug}/records/{record_id}/custom-insights/{id}',
      path: {
        id: id,
        slug: slug,
        record_id: recordId,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Translate Record Custom Insight
   * @param id
   * @param slug
   * @param recordId
   * @param requestBody
   * @returns RecordCustomInsight Successful Response
   * @throws ApiError
   */
  public translateRecordCustomInsightV1WorkspacesSlugRecordsRecordIdCustomInsightsIdTranslationPost(
    id: number,
    slug: string,
    recordId: number,
    requestBody: RecordCustomInsightTranslateRequest
  ): CancelablePromise<RecordCustomInsight> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/v1/workspaces/{slug}/records/{record_id}/custom-insights/{id}/translation',
      path: {
        id: id,
        slug: slug,
        record_id: recordId,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * List Record Export Histories
   * @param slug
   * @param recordId
   * @returns RecordExportHistory Successful Response
   * @throws ApiError
   */
  public listRecordExportHistoriesV1WorkspacesSlugRecordsRecordIdExportHistoriesGet(
    slug: string,
    recordId: number
  ): CancelablePromise<Array<RecordExportHistory>> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/v1/workspaces/{slug}/records/{record_id}/export-histories',
      path: {
        slug: slug,
        record_id: recordId,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Delete Record Export History
   * @param historyId
   * @param slug
   * @param recordId
   * @returns void
   * @throws ApiError
   */
  public deleteRecordExportHistoryV1WorkspacesSlugRecordsRecordIdExportHistoriesHistoryIdDelete(
    historyId: number,
    slug: string,
    recordId: number
  ): CancelablePromise<void> {
    return this.httpRequest.request({
      method: 'DELETE',
      url: '/v1/workspaces/{slug}/records/{record_id}/export-histories/{history_id}',
      path: {
        history_id: historyId,
        slug: slug,
        record_id: recordId,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }
}
